import {
	IconButton,
	List,
	ListItem,
	ListItemText,
	ListSubheader,
	Stack,
	Typography,
} from '@mui/material';

import React, { useEffect, useState } from 'react';

import { useSelector } from 'react-redux';

import { useLocation } from 'react-router-dom';

import { ReactComponent as DeleteIcon } from 'common/assets/images/delete-icon.svg';
import ChipComponent from 'common/components/Chip/Chip';
import CommonTable from 'common/components/Table/CommonTable';
import { Column } from 'common/components/Table/CommonTableStyles';
import { MODULES } from 'common/constants';
import { rrcpDashboardRoutes } from 'common/constants/rrcp-dashboard-routes';
import { RootState } from 'common/store/AppStore';

import styles from './UserList.module.scss';

interface IUserListOptionsProps {
	firstName: string;
	lastName: string;
	status: string;
	id: string;
	email: string;
	displayLabel?: string;
	checked?: boolean;
	userId: string;
}

interface IPermissionsModules {
	usersOptions: IUserListOptionsProps[];
	handleDelete: (data: string) => void;
	sharedBy?: string;
}

const UsersTable: React.FC<IPermissionsModules> = ({
	usersOptions,
	handleDelete,
	sharedBy,
}: IPermissionsModules) => {
	const location = useLocation();
	const [hoveredItemId, setHoveredItemId] = useState<string>();
	const [hoverItem, setHoverAction] = useState<boolean>(false);
	const [currentRoute, setCurrentRouteUrl] = useState<string>('');
	const { iconColorGray } = styles;

	const hoverAction = (id: string) => {
		setHoveredItemId(id);
		setHoverAction(true);
	};
	const blurAction = () => {
		setHoveredItemId('');
		setHoverAction(false);
	};

	const loggedInUser = useSelector(
		(state: RootState) => state.common.loggedInUser,
	);

	const getModuleExist = (module: string) => {
		const modules = loggedInUser?.modulePermissions || [];
		return modules.some((element) => element.moduleName === module);
	};
	const handleDeleteIcon = () => {
		let showDeleteIcon = true;
		const url = `/${rrcpDashboardRoutes.simulationArena}`;
		// if the record is not shared before, the user can always delete
		if (
			currentRoute.startsWith(url) &&
			getModuleExist(MODULES.AI_SIMULATION_AND_EXPERIMENTATION) &&
			sharedBy
		) {
			showDeleteIcon = loggedInUser?.email === sharedBy;
		}
		return showDeleteIcon;
	};
	const columns: readonly Column[] = [
		{
			id: 'firstName',
			label: 'Users',
			disablePadding: false,
			isSortable: false,
			render: (params: any) => (
				<Typography
					data-testid="user-name"
					className={`${styles['users-name']}`}
				>
					{`${params?.firstName} ${params?.lastName}`}
				</Typography>
			),
		},
		{
			id: 'status',
			label: 'Status',
			disablePadding: false,
			isSortable: false,
			render: (params: any) => (
				<Stack direction="row">
					<ChipComponent label={params?.status} customcolor={params?.status} />
				</Stack>
			),
		},
		{
			id: 'userId',
			label: 'Action',
			disablePadding: false,
			isSortable: false,
			render: (params: any) => (
				<List
					data-testid="status-column"
					onMouseOver={() => {
						hoverAction(params?.id);
					}}
					onFocus={() => hoverAction(params?.id)}
					onMouseOut={() => blurAction()}
				>
					{hoverItem && hoveredItemId === params?.id && handleDeleteIcon() && (
						<IconButton
							onClick={() => handleDelete(params?.userId)}
							className={styles['workflow-list-item-delete']}
							data-testid="delete-action"
						>
							<DeleteIcon
								data-testid="delete-icon"
								className={styles['status-delete-stack']}
								fill={iconColorGray}
							/>
						</IconButton>
					)}
				</List>
			),
		},
	];

	useEffect(() => {
		setCurrentRouteUrl(location?.pathname);
	}, [location]);
	return (
		<div className={styles['groups-table-container']}>
			{usersOptions?.length > 0 ? (
				<div className={styles['userslist-grid-container']}>
					<CommonTable
						rows={usersOptions}
						columns={columns}
						enableHeaderColor
						hidePagination
						tableStyles={{ maxHeight: '15rem !important' }}
						customStylesEnabled
					/>
				</div>
			) : (
				<div>
					<List
						dense
						data-testid="main-list"
						className={styles['users-list']}
						subheader={
							<ListSubheader className={styles['users-list-sublist']}>
								<ListItem disablePadding>
									<ListItemText
										id="name"
										primary="User"
										className={styles['users-list-sublistwidth']}
									/>
									<ListItemText
										id="access"
										primary="Status"
										className={styles['users-list-sublistwidth']}
									/>
									<ListItemText
										id="action"
										primary="Action"
										className={styles['users-list-sublistwidth']}
									/>
								</ListItem>
							</ListSubheader>
						}
					>
						<Typography className={styles['no-data']}>
							No users added
						</Typography>
					</List>
				</div>
			)}
		</div>
	);
};

export default UsersTable;
